// /portals  (canonical)  /welcome  (back-compat alias)
//
// Portal selector. Visitors land here after /signup completes the
// WhatsApp OTP and persists a fresh ROLE_USER token, OR via a fallback
// when /me/patient-prospect/initialize fails post-signup. The page asks
// "what do you want to do here?" and routes to one of three places.
//
// Naming: the canonical URL is /portals because that's what this page
// semantically is — a portal selector. It deserves its own top-level
// route, not tied to any one portal's scope (care, wellness, clinician).
// /welcome is kept as a backward-compat alias for existing bookmarks
// and any reminder emails that already shipped with /welcome links.
//
//   1) "I want care from a specialist" → calls
//      /api/secure/me/patient-prospect/initialize with the ASSESSMENT
//      pathway as a placeholder, which gives the user
//      ROLE_PATIENT_PROSPECT and a CareIntakeCase. They land on
//      /care/get-started so they can pick the actual pathway and
//      complete the rest of the funnel; from there it's the existing
//      /care/status flow with the progressive intake form.
//
//   2) "I'm here for the wellness app" → keep ROLE_USER and bounce to
//      the consumer home (/), where the existing wellness onboarding
//      kicks in.
//
//   3) "I'm a clinician" → quiet link to /join (clinician application
//      flow). Intentionally not a full-size card — most signups will
//      fall in path 1 or 2.
//
// Why this exists: previously the OTP-success path either dropped
// users at the consumer home (wellness onboarding) or the
// patient-prospect funnel. There was no visible point at which the
// user got to choose. New patients ended up in wellness onboarding by
// accident, and wellness users got pulled into prospect funnels by
// accident. /welcome makes the choice explicit.
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  Container,
  Eyebrow,
  Lede,
} from './sections'
import {
  ArrowRight,
  CheckCircle2,
  HeartPulse,
  ShieldCheck,
  Stethoscope,
} from './icons'
import {
  isLoggedIn,
  signupPatientProspect,
  getStoredAuthorities,
} from 'src/consumer/services/patientProspectService'

const SIGNUP_PROFILE_KEY = 'aumhumSignupProfileDraft'

function readSignupProfile() {
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_PROFILE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // 24h TTL — this draft is short-lived hand-off data, not a long
    // term store. Past TTL, behave as if there was no draft so old
    // sessions don't accidentally pollute later signups.
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > 24 * 60 * 60 * 1000) {
      window.sessionStorage.removeItem(SIGNUP_PROFILE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function clearSignupProfile() {
  try {
    window.sessionStorage.removeItem(SIGNUP_PROFILE_KEY)
  } catch {
    // ignore
  }
}

export default function WelcomeView() {
  const navigate = useNavigate()
  const profile = useMemo(readSignupProfile, [])
  const signedIn = useMemo(isLoggedIn, [])

  const [busyPath, setBusyPath] = useState(null) // 'care' | 'wellness' | null
  const [error, setError] = useState('')

  // Defensive redirects:
  //  - If the visitor reached /welcome without being signed in (bookmark,
  //    refreshed after expiry), bounce to /signup. The orchestration
  //    here only makes sense for an authenticated brand-new account.
  //  - If they already have a role beyond ROLE_USER (returning patient,
  //    prospect, professional), they're not a fresh signup — drop them
  //    on a sensible home rather than asking the welcome question again.
  useEffect(() => {
    if (!signedIn) {
      navigate('/signup', { replace: true })
      return
    }
    const auths = getStoredAuthorities()
    const hasEstablishedRole = auths.some((a) => a !== 'ROLE_USER')
    if (hasEstablishedRole) {
      if (auths.includes('ROLE_PATIENT')) {
        window.location.replace('/patient-home')
      } else if (auths.includes('ROLE_PATIENT_PROSPECT')) {
        window.location.replace('/care/status')
      } else {
        window.location.replace('/')
      }
    }
  }, [signedIn, navigate])

  const firstName = (profile?.name || '').split(' ')[0]

  // "I want care" → seed a prospect with the ASSESSMENT pathway as a
  // placeholder so the user gets ROLE_PATIENT_PROSPECT immediately;
  // /care/get-started lets them pick the actual pathway. The "Help me
  // figure it out" tile in PathwayPicker also maps to ASSESSMENT, so
  // this is identical to the visitor having selected that tile by
  // hand. We pass the demographics they just typed at /signup so the
  // care-status form doesn't ask twice.
  const goCareFunnel = async () => {
    if (busyPath) return
    setError('')
    setBusyPath('care')
    try {
      await signupPatientProspect({
        name: profile?.name || null,
        mobile: profile?.mobile || null,
        email: profile?.email || null,
        // Placeholder pathway — the user will reconfirm or change at
        // /care/get-started. ASSESSMENT routes to the care coordinator
        // by default, which is the safe fallback.
        pathway: 'ASSESSMENT',
        specialtyTag: 'triage',
        subjectType: 'SELF',
        age: profile?.age ?? null,
        gender: profile?.gender || null,
        source: 'WEB',
        bookingIntent: 'welcome',
      })
      clearSignupProfile()
      // Hard nav so the auth-aware shell reloads with the freshly
      // issued ROLE_PATIENT_PROSPECT-bearing token (see backend
      // initializeForLoggedInUser → it ships a refreshed JWT in the
      // response, picked up by signupPatientProspect's persistAuthSession
      // call). /care/get-started will let them confirm or change the
      // placeholder pathway.
      window.location.assign('/care/get-started')
    } catch (e) {
      setBusyPath(null)
      setError(
        e?.message ||
          'We could not start your care request just now. Please try again.',
      )
    }
  }

  const goWellnessApp = () => {
    if (busyPath) return
    setBusyPath('wellness')
    clearSignupProfile()
    // Wellness onboarding lives at the consumer home — RootLanding will
    // route them to AppHomepage which kicks off the existing
    // ROLE_USER onboarding.
    window.location.assign('/')
  }

  return (
    <main id="main">
      <section className="as-care-hero as-care-hero--centered">
        <Container>
          <div className="as-care-hero-stack">
            <Eyebrow>Welcome to AUMHUM</Eyebrow>
            <h1 className="as-h1 as-care-hero-title">
              {firstName ? `Hi, ${firstName} — ` : ''}what brings you here?
            </h1>
            <Lede className="as-care-hero-lede">
              Pick the path that fits. You can change your mind later — your
              account stays the same.
            </Lede>

            {/* Two big paths sit inside the hero. The clinician path is
                a quiet link beneath, intentionally — most signups will
                land in care or wellness. */}
            <div className="bc-paths wv-paths" role="group" aria-label="Choose your path">
              <button
                type="button"
                className="bc-path-card bc-path-card--new wv-path-button"
                onClick={goCareFunnel}
                disabled={!!busyPath}
                aria-busy={busyPath === 'care' || undefined}
              >
                <div className="bc-path-icon">
                  <Stethoscope />
                </div>
                <div className="bc-path-body">
                  <p className="as-care-kicker">Get matched with a specialist</p>
                  <h2 className="as-h3">I want care for a health concern</h2>
                  <p className="as-p">
                    We&rsquo;ll match you to a vetted specialist — therapist,
                    psychiatrist, endocrinologist, pediatrician, or a coordinator
                    who&rsquo;ll help you decide.
                  </p>
                  <span className="as-btn as-btn-primary bc-path-cta">
                    {busyPath === 'care' ? 'Setting up…' : 'Continue to care'} <ArrowRight />
                  </span>
                </div>
              </button>

              <button
                type="button"
                className="bc-path-card bc-path-card--returning wv-path-button"
                onClick={goWellnessApp}
                disabled={!!busyPath}
                aria-busy={busyPath === 'wellness' || undefined}
              >
                <div className="bc-path-icon bc-path-icon--alt">
                  <HeartPulse />
                </div>
                <div className="bc-path-body">
                  <p className="as-care-kicker">For self-care + mental wellness</p>
                  <h2 className="as-h3">I&rsquo;m here for the AUMHUM app</h2>
                  <p className="as-p">
                    Daily practices, sleep, focus, journaling, breathing — guided
                    by clinicians, on your schedule. No appointments needed.
                  </p>
                  <span className="as-btn as-btn-secondary bc-path-cta">
                    {busyPath === 'wellness' ? 'Loading…' : 'Continue to wellness'} <ArrowRight />
                  </span>
                </div>
              </button>
            </div>

            {error ? (
              <div className="pps-error" role="alert">{error}</div>
            ) : null}

            {/* Clinician path — quiet, single-line. Doesn't compete with
                the two big patient paths. */}
            <p className="wv-clinician-link">
              Joining as a clinician?{' '}
              <Link to="/join">Apply to AUMHUM&rsquo;s care network →</Link>
            </p>

            <ul className="bc-howit-trust" aria-label="What signing up gets you">
              <li>
                <CheckCircle2 /> Your details stay with the assigned care team only
              </li>
              <li>
                <CheckCircle2 /> Switching paths later doesn&rsquo;t lose your account
              </li>
              <li>
                <ShieldCheck /> Confidential, encrypted, and reviewed by our team
              </li>
            </ul>
          </div>
        </Container>
      </section>
    </main>
  )
}
